import { forwardRef, Fragment } from 'react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { FAMILY_CARD_FONT_FAMILY } from '../family-card-form/familyCardFont';
import { useFitFontSize, VALUE_INK_SHADOW } from '../shared/formFieldEmphasis';
import { buildVoterRegistrationSheet, combDigits } from './voterRegistrationFormEngine';
import {
    BASELINE_RATIO,
    BOX_LABEL_BASELINE_RATIO,
    CELL_BASELINE_RATIO,
    CELL_VALUE_TYPE,
    EMBLEM,
    LEADER,
    LINE_BOX,
    PAGE1_COMBS,
    PAGE1_ROWS,
    PAGE2_COMBS,
    PAGE2_ROWS,
    PAGE_HEIGHT,
    PAGE_WIDTH,
    SEPARATORS,
    TYPE,
    VALUE_INSET,
    VALUE_LIFT,
    VALUE_TYPE,
} from './voterRegistrationMetrics';

export const VOTER_REGISTRATION_PAGE_WIDTH = PAGE_WIDTH;
export const VOTER_REGISTRATION_PAGE_HEIGHT = PAGE_HEIGHT;

const LEADER_TEXT = LEADER.char.repeat(LEADER.count);

const PAGE_STYLE = {
    position: 'relative',
    width: PAGE_WIDTH,
    height: PAGE_HEIGHT,
    background: '#fff',
    color: '#111',
    boxSizing: 'border-box',
    fontFamily: FAMILY_CARD_FONT_FAMILY,
    fontSize: TYPE.body.size,
    overflow: 'hidden',
};

/** A measured baseline becomes the CSS top of the line box. */
const topFor = (baseline, size) => baseline - size * BASELINE_RATIO;

/**
 * Rows are placed by baseline. A line box 1.5 em tall leaves room for dots and
 * descenders inside the hidden overflow — padding would do the same in the
 * browser, but html2canvas shifts the text by it, so the export drifted down.
 */
const textStyle = (size, weight) => ({
    position: 'absolute',
    fontSize: size,
    fontWeight: weight,
    lineHeight: LINE_BOX,
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
});

/**
 * Printed text of the form, anchored by baseline and by left edge or centre.
 *
 * A `limit` is the room the sheet leaves before the next rule, box or word on
 * that line. Kalpurush sets a few of these labels wider than the face the form
 * was printed in, and the form is the fixed thing — so the label is set down
 * until it fits rather than the box being made smaller to take it.
 */
function Line({ y, x, center, size = TYPE.body.size, weight = TYPE.body.weight, limit, children }) {
    const [ref, fitted] = useFitFontSize(size, size * 0.78, [children, limit, size]);
    const used = limit != null ? fitted : size;
    const style = { ...textStyle(used, weight), top: topFor(y, used) };
    if (center != null) {
        style.left = center;
        style.transform = 'translateX(-50%)';
    } else {
        style.left = x;
    }
    if (limit != null) style.maxWidth = limit;
    return <span ref={limit != null ? ref : undefined} style={style}>{children}</span>;
}

/** One printed leader: real dots, clipped to the span the form gives it. */
function Leader({ y, from, to, size = TYPE.body.size }) {
    return (
        <span
            aria-hidden
            data-leader
            style={{
                ...textStyle(size, TYPE.body.weight),
                top: topFor(y, size),
                left: from,
                width: Math.max(0, to - from),
                overflow: 'hidden',
                letterSpacing: LEADER.letterSpacing,
            }}
        >
            {LEADER_TEXT}
        </span>
    );
}

/**
 * A typed answer, sitting just above its dotted line and clipped to it. It
 * prints bigger and heavier than the form's own text, and drops back toward the
 * body size only when the blank is too short for the answer.
 */
function Value({ y, from, to, children }) {
    const maxWidth = Math.max(0, to - from - VALUE_INSET - 2);
    const [ref, size] = useFitFontSize(VALUE_TYPE.size, VALUE_TYPE.minSize, [children, maxWidth]);

    if (!children) return null;
    return (
        <span
            ref={ref}
            data-field-value
            data-filled="true"
            style={{
                ...textStyle(size, VALUE_TYPE.weight),
                top: topFor(y - VALUE_LIFT, size),
                // A little air after the printed label, like a written form.
                left: from + VALUE_INSET,
                maxWidth,
                textShadow: VALUE_INK_SHADOW,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}
        >
            {children}
        </span>
    );
}

/**
 * One combed row — the boxes the form prints, and what goes in them.
 *
 * A `tick` group is a single square that takes a ✓; everything else takes one
 * digit per cell. The cells are drawn from the band's span rather than from
 * individual rules, because the scan's own cell rules are evenly spaced inside
 * each band to within a quarter of a point.
 *
 * The digits are drawn the same way as every other line — absolutely placed on
 * a baseline — because html2canvas does not centre flex children, which pushed
 * them to the bottom of the boxes on export.
 */
function Comb({ comb, value, t }) {
    const { top, height, left, right, cells, tick, label, text: inner } = comb;
    const width = (right - left) / cells;
    const printed = label ? t(`voterRegistrationForm.preview.${label}`) : inner;
    // A box the form already prints a word in cannot also take a ✓ — the mark
    // would land on top of the word — so a chosen one is thickened instead.
    const marked = tick && Boolean(value);
    const text = tick ? (printed || (value ? '✓' : '')) : combDigits(value, cells);
    const baseline = top + height * CELL_BASELINE_RATIO;

    return (
        <>
            {Array.from({ length: cells }, (_, index) => (
                <span
                    // eslint-disable-next-line react/no-array-index-key
                    key={`box-${index}`}
                    aria-hidden
                    style={{
                        position: 'absolute',
                        left: left + index * width,
                        top,
                        width,
                        height,
                        border: `${marked && printed ? 2 : 1}px solid #111`,
                        // Neighbours share a rule, the way the printed table does.
                        borderLeftWidth: index === 0 ? (marked && printed ? 2 : 1) : 0,
                        boxSizing: 'border-box',
                    }}
                />
            ))}
            {printed ? (
                // The word the form itself prints in the box — part of the sheet,
                // not an answer, so it keeps the body size.
                <span
                    style={{
                        ...textStyle(TYPE.small.size, marked ? 700 : TYPE.small.weight),
                        top: topFor(top + height * BOX_LABEL_BASELINE_RATIO, TYPE.small.size),
                        left,
                        width: right - left,
                        textAlign: 'center',
                    }}
                >
                    {printed}
                </span>
            ) : Array.from({ length: cells }, (_, index) => {
                const char = text[index];
                if (!char) return null;
                return (
                    <span
                        // eslint-disable-next-line react/no-array-index-key
                        key={`digit-${index}`}
                        data-field-value
                        data-filled="true"
                        style={{
                            ...textStyle(CELL_VALUE_TYPE.size, CELL_VALUE_TYPE.weight),
                            textShadow: VALUE_INK_SHADOW,
                            top: topFor(baseline, CELL_VALUE_TYPE.size),
                            // Centred by width, not by transform: html2canvas
                            // drops transforms and the digits slid off centre.
                            left: left + index * width,
                            width,
                            textAlign: 'center',
                        }}
                    >
                        {char}
                    </span>
                );
            })}
        </>
    );
}

/** The dotted rule the slip and the receipt are torn off along. */
function Separator({ y, from, to }) {
    return <Leader y={y} from={from} to={to} />;
}

/**
 * Items ১১-১৩ print স্বামী and স্ত্রী as two boxes joined by a stroke, so the
 * applicant ticks whichever applies. The stroke is the form's own, not a value.
 */
function SpouseSlash({ top, height, from, to }) {
    return (
        <span
            aria-hidden
            style={{
                position: 'absolute',
                left: from,
                top: top + height,
                width: Math.hypot(to - from, height),
                height: 0,
                borderTop: '1px solid #111',
                transformOrigin: 'left top',
                transform: `rotate(${-Math.atan2(height, to - from) * (180 / Math.PI)}deg)`,
            }}
        />
    );
}

/** The label, leader and value for every row on a page. */
function Rows({ rows, t, d }) {
    return rows.map((row, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={`${row.y}-${row.key || row.value || index}`}>
            {row.key ? (
                <Line
                    y={row.y}
                    x={row.x}
                    center={row.center}
                    limit={row.limit}
                    size={TYPE[row.size || 'body'].size}
                    weight={TYPE[row.size || 'body'].weight}
                >
                    {t(`voterRegistrationForm.preview.${row.key}`)}
                </Line>
            ) : null}
            {row.from != null ? <Leader y={row.y} from={row.from} to={row.to} /> : null}
            {row.value ? (
                <Value y={row.y} from={row.from} to={row.to}>{d[row.value]}</Value>
            ) : null}
        </Fragment>
    ));
}

function PageOne({ d, t }) {
    return (
        <div data-voter-registration-page style={PAGE_STYLE}>
            <img
                src="/images/ec-logo.png"
                alt=""
                aria-hidden
                style={{
                    position: 'absolute',
                    left: EMBLEM.left,
                    top: EMBLEM.top,
                    width: EMBLEM.width,
                    height: EMBLEM.height,
                }}
            />

            {PAGE1_COMBS.map((comb, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Comb key={`comb-${index}`} comb={comb} value={comb.value ? d[comb.value] : ''} t={t} />
            ))}
            <SpouseSlash top={339} height={19} from={127} to={139} />
            <SpouseSlash top={367} height={15} from={124} to={135.75} />
            <Rows rows={PAGE1_ROWS} t={t} d={d} />
            {SEPARATORS.filter((s) => s.page === 1).map((s) => (
                <Separator key={s.y} y={s.y} from={s.from} to={s.to} />
            ))}
        </div>
    );
}

function PageTwo({ d, t }) {
    return (
        <div data-voter-registration-page style={PAGE_STYLE}>
            {PAGE2_COMBS.map((comb, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Comb key={`comb-${index}`} comb={comb} value={comb.value ? d[comb.value] : ''} t={t} />
            ))}
            <Rows rows={PAGE2_ROWS} t={t} d={d} />
            {SEPARATORS.filter((s) => s.page === 2).map((s) => (
                <Separator key={s.y} y={s.y} from={s.from} to={s.to} />
            ))}
        </div>
    );
}

function VoterRegistrationFormPreview({ form }, ref) {
    const { t } = useTranslation();
    const d = buildVoterRegistrationSheet(form);

    return (
        <div
            ref={ref}
            data-voter-registration-document
            style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
            <PageOne d={d} t={t} />
            <PageTwo d={d} t={t} />
        </div>
    );
}

export default forwardRef(VoterRegistrationFormPreview);
